import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Chip,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import { Download, Description } from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import { format } from 'date-fns';
import { MarketReport } from '@horwath-htl/shared-types';
import { ApiClient } from '../../services/api';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { formatFileSize } from '../../utils/formatters';
import { API_URL } from '../../utils/config';

interface ReportCardProps {
  report: MarketReport;
}

export const ReportCard: React.FC<ReportCardProps> = ({ report }) => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [showSuccess, setShowSuccess] = React.useState(false);
  const [showError, setShowError] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');

  const apiClient = new ApiClient({ authenticatedFetch });

  const downloadMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.get(
        `${API_URL}/api/marketcompass/markets/${report.marketId}/reports/${report.id}/download-url`
      );
      return response.data;
    },
    onSuccess: (data) => {
      // Open download URL in new tab
      window.open(data.downloadUrl, '_blank');
      setShowSuccess(true);
    },
    onError: (error: any) => {
      setErrorMessage(
        error.response?.data?.error || 'Failed to generate download link'
      );
      setShowError(true);
    }
  });

  const formatReportMonth = (month: number): string => {
    const year = Math.floor(month / 100);
    const monthNum = month % 100;
    return format(new Date(year, monthNum - 1), 'MMMM yyyy');
  };

  return (
    <>
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'start', mb: 2 }}>
          <Description sx={{ mr: 1, color: 'primary.main', mt: 0.5 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" component="h3" gutterBottom>
              {report.marketName}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {report.continentName} › {report.countryName}
            </Typography>
          </Box>
        </Box>

        <Chip
          label={formatReportMonth(report.reportMonth)}
          size="small"
          color="primary"
          sx={{ mb: 2 }}
        />

        <Typography variant="body2" color="text.secondary" paragraph>
          {report.shortDescription}
        </Typography>

        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            File size: {formatFileSize(report.fileSizeBytes)}
          </Typography>
        </Box>
      </CardContent>

      <CardActions>
        <Button
          startIcon={
            downloadMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              <Download />
            )
          }
          onClick={() => downloadMutation.mutate()}
          disabled={downloadMutation.isPending}
          fullWidth
          variant="contained"
        >
          Download PDF
        </Button>
      </CardActions>
    </Card>

    {/* Success Snackbar */}
    <Snackbar
      open={showSuccess}
      autoHideDuration={3000}
      onClose={() => setShowSuccess(false)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        onClose={() => setShowSuccess(false)}
        severity="success"
        sx={{ width: '100%' }}
      >
        Download started
      </Alert>
    </Snackbar>

    {/* Error Snackbar */}
    <Snackbar
      open={showError}
      autoHideDuration={4000}
      onClose={() => setShowError(false)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        onClose={() => setShowError(false)}
        severity="error"
        sx={{ width: '100%' }}
      >
        {errorMessage}
      </Alert>
    </Snackbar>
    </>
  );
};